import { onGoalReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';

import { useGoalTree } from '../../hooks/use-goal-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './goal-grouping-tasks-list.style';
import { GoalRenderData, GoalRenderDataContext } from './goal-render-context';
import { GoalRenderItem } from './goal-render-item';

const goalDragTypes = [TaskListItemTypes.Task, TaskListItemTypes.Goal];
const taskItemTypes = [TaskListItemTypes.Task];

const dropTypes = {
  [TaskListItemTypes.Root]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Task],
  [TaskListItemTypes.GoalDeadline]: [],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

export const GoalGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
  canReorderGoals,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const goalGroupingRootId = 'goal-grouping-root';

  const treeItems = useGoalTree({
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNoGoalGroup: groupSettings.hideNoGoalGroup,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
    rootId: goalGroupingRootId,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onGoalReorder({
      updatedItems,
      oldItems: Object.values(treeItems),
      tasksMap,
      rootSectionId: rootId,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  const renderData = useMemo<GoalRenderData>(
    () => ({
      groupSettings,
      tasksMap,
      tasks,
      goals,
      lifeAreas,
      today,
      denseFormat,
      defaultNewTaskDate,
    }),
    [
      groupSettings,
      tasksMap,
      tasks,
      goals,
      lifeAreas,
      today,
      denseFormat,
      defaultNewTaskDate,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <GoalRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={goalGroupingRootId}
          items={treeItems}
          renderItem={GoalRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={canReorderGoals ? goalDragTypes : taskItemTypes}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
          dropTypes={dropTypes}
        />
      </GoalRenderDataContext.Provider>
    </Styled.Container>
  );
};
